import React, { useEffect } from 'react'
import { Flex, Typography } from 'antd'
import {
  Content,
  PageHeader,
  ReloadButton,
  TabButton,
  TasksTabs,
  SearchContainer,
} from './styled'
import { Icon } from '../Icon/Icon'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import { MediumInput } from '@/shared/ui/components/Input'
import { useAuthStore } from '@/app/store/authStore'
import { ROOT_TRANSFER_ACC } from '@/shared/config/admins'

const { Title, Text } = Typography

interface PageLayoutProps {
  title: string
  subtitle?: string
  onRefresh?: () => void
  children: React.ReactNode
  showRefreshButton?: boolean
  activeTab?: string
  onTabChange?: (key: string) => void
  tabs?: Array<{
    key: string
    label: string
    disabled?: boolean
    icon?: string
  }>
  showSearch?: boolean
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  subtitle,
  onRefresh,
  children,
  showRefreshButton = true,
  activeTab,
  onTabChange,
  tabs = [],
  showSearch = false,
  searchPlaceholder = 'Введите запрос',
  searchValue = '',
  onSearchChange,
}) => {
  const { isSm, isXs } = useBreakpoint()
  const { user } = useAuthStore()
  const shouldShowRefreshButton = showRefreshButton && onRefresh
  const hasTabs = tabs.length > 0
  const isAdmin = user?.username === ROOT_TRANSFER_ACC

  const isMobile = isXs || isSm

  useEffect(() => {
    const handleOrientationChange = () => {
      window.dispatchEvent(new Event('resize'))
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    }

    window.addEventListener('orientationchange', handleOrientationChange)

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])

  return (
    <Content>
      <PageHeader
        vertical
        align={isMobile && shouldShowRefreshButton ? 'start' : 'center'}
        gap={8}
      >
        {hasTabs ? (
          <Flex
            style={{ width: '100%', paddingTop: isMobile && !isAdmin ? 34 : 0 }}
            align={!isMobile ? 'center' : 'start'}
            justify="space-between"
            wrap="wrap"
            gap={!isMobile ? 16 : 32}
            vertical={isMobile}
          >
            {isAdmin ? (
              <TasksTabs>
                {tabs.map((tab) => (
                  <TabButton
                    size="small"
                    key={tab.key}
                    $active={activeTab === tab.key}
                    onClick={() =>
                      !tab.disabled && onTabChange && onTabChange(tab.key)
                    }
                    disabled={tab.disabled}
                  >
                    {tab.icon && <Icon name={tab.icon} size={16} />}
                    {tab.label}
                  </TabButton>
                ))}
              </TasksTabs>
            ) : (
              <div></div>
            )}
            <Flex gap={32} style={{ width: !isMobile ? '50%' : '100%' }}>
              {showSearch && (
                <SearchContainer>
                  <MediumInput
                    placeholder={searchPlaceholder}
                    prefix={<Icon name="input_search" size={16} />}
                    value={searchValue}
                    onChange={(e) =>
                      onSearchChange && onSearchChange(e.target.value)
                    }
                  />
                </SearchContainer>
              )}

              {shouldShowRefreshButton && (
                <ReloadButton
                  icon={<Icon name="reload" size={16} />}
                  onClick={onRefresh}
                />
              )}
            </Flex>
          </Flex>
        ) : (
          <Flex vertical gap={4}>
            <Title level={2} style={{ margin: 0 }}>
              {title}
            </Title>
            {subtitle && <Text type="secondary">{subtitle}</Text>}
          </Flex>
        )}
      </PageHeader>
      {children}
    </Content>
  )
}
